// Smart Backup Service - Backs up deleted data before removal
// Keeps Firebase clean while preserving deleted data safely
import { db } from '../config/firebase';
import { doc, setDoc, getDoc, updateDoc, arrayUnion, serverTimestamp } from 'firebase/firestore';
import firebaseAuthService from './firebaseAuthService';

class BackupService {
  constructor() {
    this.isOnline = navigator.onLine;
    
    // Listen for online/offline changes
    window.addEventListener('online', () => {
      this.isOnline = true;
      console.log('🌐 BackupService: Back online');
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
      console.log('📱 BackupService: Gone offline');
    });
  }

  /**
   * Get current farm ID
   */
  getCurrentFarmId() {
    const session = firebaseAuthService.getCurrentSession();
    return session?.farm?.id || null;
  }

  /**
   * Backup data before deletion (optimized for speed)
   * @param {string} dataType - Type of data being deleted (parcelsData, inventoryData, etc.)
   * @param {*} dataToDelete - The actual data being deleted
   * @param {string} reason - Reason for deletion (user_delete, bulk_delete, etc.)
   * @param {Object} metadata - Additional metadata about the deletion
   */
  async backupBeforeDeletion(dataType, dataToDelete, reason = 'user_delete', metadata = {}) {
    const farmId = this.getCurrentFarmId();
    if (!farmId) {
      console.warn('⚠️ BackupService: No farm ID found, skipping backup');
      return false;
    }

    // Always store locally first (instant)
    const localBackupId = this.storeBackupLocally(farmId, dataType, dataToDelete, reason, metadata);
    
    // If offline, return immediately
    if (!this.isOnline) {
      console.log('📱 BackupService: Offline, backup stored locally');
      return localBackupId;
    }

    // For online: start Firebase backup in background (don't wait)
    this.backupToFirebaseInBackground(farmId, dataType, dataToDelete, reason, metadata)
      .then(backupId => {
        console.log(`✅ BackupService: ${dataType} backed up to Firebase in background`);
      })
      .catch(error => {
        console.error('❌ BackupService: Background backup failed:', error);
        // Local backup is still available as fallback
      });

    // Return immediately with local backup ID
    return localBackupId;
  }

  /**
   * Background Firebase backup (non-blocking)
   */
  async backupToFirebaseInBackground(farmId, dataType, dataToDelete, reason, metadata) {
    try {
      const backupDocRef = doc(db, 'farmBackup', farmId);
      
      // Get existing backup document
      const backupDoc = await getDoc(backupDocRef);
      let existingBackups = {};
      
      if (backupDoc.exists()) {
        existingBackups = backupDoc.data();
      }

      // Create backup entry
      const backupEntry = {
        data: dataToDelete,
        deletedAt: new Date().toISOString(),
        reason: reason,
        dataType: dataType,
        metadata: {
          ...metadata,
          farmId: farmId,
          userAgent: navigator.userAgent,
          url: window.location.href
        },
        id: `${dataType}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      };

      // Initialize backup structure if needed
      if (!existingBackups[dataType]) {
        existingBackups[dataType] = [];
      }

      // Add new backup entry
      existingBackups[dataType].push(backupEntry);
      
      // Keep only last 50 backups per data type to prevent bloat (reduced from 100)
      if (existingBackups[dataType].length > 50) {
        existingBackups[dataType] = existingBackups[dataType].slice(-50);
      }

      // Update backup metadata
      existingBackups.lastBackup = new Date().toISOString();
      existingBackups.totalBackups = (existingBackups.totalBackups || 0) + 1;

      // Save to Firebase
      await setDoc(backupDocRef, existingBackups, { merge: true });
      
      return backupEntry.id;
      
    } catch (error) {
      console.error('❌ BackupService: Firebase backup failed:', error);
      throw error;
    }
  }

  /**
   * Store backup locally when offline
   */
  storeBackupLocally(farmId, dataType, dataToDelete, reason, metadata) {
    try {
      const localBackupKey = `backup_${farmId}`;
      const existingBackups = JSON.parse(localStorage.getItem(localBackupKey) || '{}');
      
      if (!existingBackups[dataType]) {
        existingBackups[dataType] = [];
      }

      const backupEntry = {
        data: dataToDelete,
        deletedAt: new Date().toISOString(),
        reason: reason,
        dataType: dataType,
        metadata: {
          ...metadata,
          farmId: farmId,
          storedLocally: true
        },
        id: `local_${dataType}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      };

      existingBackups[dataType].push(backupEntry);
      existingBackups.lastBackup = new Date().toISOString();
      existingBackups.needsSync = true;

      localStorage.setItem(localBackupKey, JSON.stringify(existingBackups));
      
      console.log(`📱 BackupService: ${dataType} backed up locally`);
      return backupEntry.id;
      
    } catch (error) {
      console.error('❌ BackupService: Local backup failed:', error);
      return false;
    }
  }

  /**
   * Sync local backups to Firebase when back online
   */
  async syncLocalBackups() {
    const farmId = this.getCurrentFarmId();
    if (!farmId || !this.isOnline) return;

    try {
      const localBackupKey = `backup_${farmId}`;
      const localBackups = JSON.parse(localStorage.getItem(localBackupKey) || '{}');
      
      if (!localBackups.needsSync) return;

      console.log('🔄 BackupService: Syncing local backups to Firebase...');
      
      const backupDocRef = doc(db, 'farmBackup', farmId);
      const backupDoc = await getDoc(backupDocRef);
      let existingBackups = backupDoc.exists() ? backupDoc.data() : {};

      // Merge local backups with Firebase backups
      for (const [dataType, backups] of Object.entries(localBackups)) {
        if (dataType === 'needsSync' || dataType === 'lastBackup' || !Array.isArray(backups)) continue;
        
        if (!existingBackups[dataType]) {
          existingBackups[dataType] = [];
        }
        
        // Add local backups that aren't already in Firebase
        const localOnlyBackups = backups.filter(backup => 
          backup.id.startsWith('local_') && 
          !existingBackups[dataType].some(existing => existing.id === backup.id)
        );
        
        existingBackups[dataType].push(...localOnlyBackups);
      }

      // Update Firebase
      await setDoc(backupDocRef, existingBackups, { merge: true });
      
      // Clear local backup sync flag
      delete localBackups.needsSync;
      localStorage.setItem(localBackupKey, JSON.stringify(localBackups));
      
      console.log('✅ BackupService: Local backups synced to Firebase');
      
    } catch (error) {
      console.error('❌ BackupService: Failed to sync local backups:', error);
    }
  }

  /**
   * Get backup statistics
   */
  async getBackupStats() {
    const farmId = this.getCurrentFarmId();
    if (!farmId) return null;

    try {
      const backupDocRef = doc(db, 'farmBackup', farmId);
      const backupDoc = await getDoc(backupDocRef);
      
      if (!backupDoc.exists()) {
        return {
          totalBackups: 0,
          dataTypes: [],
          lastBackup: null,
          size: 0
        };
      }

      const backupData = backupDoc.data();
      const dataTypes = Object.keys(backupData).filter(key => 
        !['lastBackup', 'totalBackups'].includes(key)
      );
      
      let totalItems = 0;
      dataTypes.forEach(dataType => {
        if (Array.isArray(backupData[dataType])) {
          totalItems += backupData[dataType].length;
        }
      });

      return {
        totalBackups: totalItems,
        dataTypes: dataTypes,
        lastBackup: backupData.lastBackup,
        size: JSON.stringify(backupData).length
      };
      
    } catch (error) {
      console.error('❌ BackupService: Failed to get backup stats:', error);
      return null;
    }
  }

  /**
   * Clean old backups (keep only last 30 days)
   */
  async cleanOldBackups(daysToKeep = 30) {
    const farmId = this.getCurrentFarmId();
    if (!farmId || !this.isOnline) return;

    try {
      console.log(`🧹 BackupService: Cleaning backups older than ${daysToKeep} days...`);
      
      const backupDocRef = doc(db, 'farmBackup', farmId);
      const backupDoc = await getDoc(backupDocRef);
      
      if (!backupDoc.exists()) return;

      const backupData = backupDoc.data();
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysToKeep);
      
      let cleanedCount = 0;
      
      for (const [dataType, backups] of Object.entries(backupData)) {
        if (!Array.isArray(backups)) continue;
        
        const filteredBackups = backups.filter(backup => {
          const backupDate = new Date(backup.deletedAt);
          return backupDate > cutoffDate;
        });
        
        if (filteredBackups.length < backups.length) {
          backupData[dataType] = filteredBackups;
          cleanedCount += backups.length - filteredBackups.length;
        }
      }

      if (cleanedCount > 0) {
        await setDoc(backupDocRef, backupData, { merge: true });
        console.log(`✅ BackupService: Cleaned ${cleanedCount} old backups`);
      }
      
    } catch (error) {
      console.error('❌ BackupService: Failed to clean old backups:', error);
    }
  }

  /**
   * Start automatic backup sync and cleanup
   */
  startAutoMaintenance() {
    // Sync local backups every 5 minutes
    setInterval(() => {
      if (this.isOnline) {
        this.syncLocalBackups();
      }
    }, 5 * 60 * 1000);

    // Clean old backups daily
    setInterval(() => {
      if (this.isOnline) {
        this.cleanOldBackups();
      }
    }, 24 * 60 * 60 * 1000);

    console.log('🔄 BackupService: Auto-maintenance started');
  }

  /**
   * Test backup system automatically (for development/verification)
   */
  async testBackupSystem() {
    const farmId = this.getCurrentFarmId();
    if (!farmId) {
      console.log('❌ BackupService Test: No farm ID found');
      return false;
    }

    try {
      console.log('🧪 BackupService: Starting automatic backup test...');
      
      // Create test data
      const testData = {
        id: 'test_' + Date.now(),
        type: 'test_parcel',
        name: 'Test Backup Parcel',
        surface: 1.5,
        owner: 'Test Owner',
        createdAt: new Date().toISOString(),
        testMarker: true
      };

      // Test backup functionality
      const backupId = await this.backupBeforeDeletion(
        'testData',
        testData,
        'automatic_test',
        {
          testRun: true,
          purpose: 'verify_backup_system'
        }
      );

      if (backupId) {
        console.log('✅ BackupService Test: Backup created successfully');
        console.log('📊 BackupService Test: Backup ID:', backupId);
        
        // Verify backup exists
        const stats = await this.getBackupStats();
        if (stats && stats.totalBackups > 0) {
          console.log('✅ BackupService Test: Backup verified in Firebase');
          console.log('📈 BackupService Test: Total backups:', stats.totalBackups);
          console.log('📂 BackupService Test: Data types:', stats.dataTypes);
          return true;
        } else {
          console.log('❌ BackupService Test: Backup not found in Firebase');
          return false;
        }
      } else {
        console.log('❌ BackupService Test: Failed to create backup');
        return false;
      }
      
    } catch (error) {
      console.error('❌ BackupService Test: Error during test:', error);
      return false;
    }
  }

  /**
   * Run comprehensive backup system verification
   */
  async runBackupVerification() {
    console.log('🔍 BackupService: Running comprehensive verification...');
    
    const results = {
      farmIdCheck: false,
      onlineCheck: false,
      firebaseConnection: false,
      backupCreation: false,
      backupRetrieval: false,
      localFallback: false
    };

    try {
      // 1. Check farm ID
      const farmId = this.getCurrentFarmId();
      results.farmIdCheck = !!farmId;
      console.log(`${results.farmIdCheck ? '✅' : '❌'} Farm ID: ${farmId || 'Not found'}`);

      // 2. Check online status
      results.onlineCheck = this.isOnline;
      console.log(`${results.onlineCheck ? '✅' : '❌'} Online status: ${this.isOnline}`);

      // 3. Test Firebase connection
      try {
        const stats = await this.getBackupStats();
        results.firebaseConnection = true;
        console.log('✅ Firebase connection: Working');
        console.log('📊 Current backup stats:', stats);
      } catch (error) {
        results.firebaseConnection = false;
        console.log('❌ Firebase connection: Failed', error.message);
      }

      // 4. Test backup creation
      if (results.farmIdCheck && results.onlineCheck && results.firebaseConnection) {
        results.backupCreation = await this.testBackupSystem();
        console.log(`${results.backupCreation ? '✅' : '❌'} Backup creation test`);
      }

      // 5. Test local fallback (simulate offline)
      const originalOnlineStatus = this.isOnline;
      this.isOnline = false;
      
      const localBackupId = this.storeBackupLocally(
        farmId,
        'testData',
        { test: 'local_fallback_test' },
        'offline_test',
        { testType: 'local_fallback' }
      );
      
      results.localFallback = !!localBackupId;
      console.log(`${results.localFallback ? '✅' : '❌'} Local fallback test`);
      
      // Restore online status
      this.isOnline = originalOnlineStatus;

      // Summary
      const passedTests = Object.values(results).filter(Boolean).length;
      const totalTests = Object.keys(results).length;
      
      console.log(`\n🎯 BackupService Verification Complete: ${passedTests}/${totalTests} tests passed`);
      console.log('📋 Detailed results:', results);
      
      if (passedTests === totalTests) {
        console.log('🎉 BackupService: All systems operational!');
      } else {
        console.log('⚠️ BackupService: Some issues detected, but core functionality should work');
      }

      return results;
      
    } catch (error) {
      console.error('❌ BackupService Verification: Failed', error);
      return results;
    }
  }
}

// Create and export singleton instance
const backupService = new BackupService();

// Start auto-maintenance
backupService.startAutoMaintenance();

// Auto-run verification in development
if (process.env.NODE_ENV === 'development') {
  // Run verification after 5 seconds to allow Firebase to initialize
  setTimeout(() => {
    backupService.runBackupVerification();
  }, 5000);
}

// Make test functions available globally for manual testing
window.testBackupService = () => backupService.runBackupVerification();

export default backupService; 